import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ContactFormContext } from "../context/contactFormContext";
import axios from "axios";

export default function ContactForm() {
  const cancelButtonRef = useRef(null);
  const { openContact, handleOpenContact } = useContext(ContactFormContext);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const [successMessage, setSuccessMessage] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFocus = (e) => {
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    const validators = {
      firstname: (value) => (value?.trim() ? null : "Firstname is required"),
      lastname: (value) => (value?.trim() ? null : "Lastname is required"),
      email: (value) => (value?.trim() ? null : "Email is required"),
      phone: (value) => {
        if (!value?.trim()) return "Phone number is required";
        const isValid = /^\+?[0-9\s\-()]{8,15}$/.test(value);
        return isValid ? null : "Please enter a valid phone number";
      },
      message: (value) => (value?.trim() ? null : "Message is required"),
    };

    for (const field in validators) {
      const error = validators[field](formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await axios.post(
        "https://formspree.io/f/xpwdzaen",
        formData
      );

      if (response?.data?.ok) {
        setSuccessMessage(true);
        setFormData({});
      } else {
        setSuccessMessage(false);
      }
    } catch (error) {
      setSuccessMessage(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={openContact} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-30 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => {
          handleOpenContact(false);
          setFormData({
            firstname: "",
            lastname: "",
            email: "",
            message: "",
          });
          setErrors({});
          setLoading(false);
          setSuccessMessage(false);
        }}>
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 mt-16 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div className="inline-block align-center bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
              <div className="bg-[#ffffff] px-6 md:px-10 py-6 md:py-10">
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleOpenContact(false);
                      setFormData({
                        firstname: "",
                        lastname: "",
                        email: "",
                        message: "",
                      });
                      setErrors({});
                      setLoading(false);
                      setSuccessMessage(false);
                    }}
                    className="shadow-lg px-4 py-2 text-red-500 font-semibold rounded-full focus:outline-none">
                    x
                  </button>
                </div>

                {successMessage ? (
                  <div className="flex flex-col items-center text-center pt-6 pb-10">
                    <div className="mb-6">
                      <svg
                        className="w-28 h-20 lg:w-44 lg:h-32"
                        viewBox="0 0 183 102"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M135.999 84.9998H152.999V89.2494C152.999 96.2826 147.284 102 140.249 102H12.7506C5.7173 102 0 96.2845 0 89.2494L0.00148226 12.7506C0.00148226 5.7173 5.71689 0 12.752 0H140.25C147.283 0 153.001 5.7154 153.001 12.7506V25.5011H136.001C134.917 25.5011 133.875 25.7353 132.919 26.0955L139.017 19.9976C140.674 18.3404 140.674 15.6414 139.017 13.984C137.36 12.3268 134.661 12.3268 133.003 13.984L85.5101 61.477C80.5373 66.4498 72.4412 66.4498 67.4695 61.477L19.9955 14.0029C18.3383 12.3458 15.6393 12.3458 13.9818 14.0029C12.3247 15.6601 12.3247 18.3591 13.9818 20.0166L61.4559 67.4906C65.6003 71.6351 71.0402 73.6955 76.4801 73.6955C81.9201 73.6955 87.36 71.6337 91.5044 67.4906L128.075 30.9203C127.732 31.8764 127.5 32.9169 127.5 34.0005V76.4998C127.5 81.1956 131.303 85.0004 135.999 85.0004L135.999 84.9998ZM169.999 50.9996H140.25C137.913 50.9996 136.001 52.9117 136.001 55.2491C136.001 57.5867 137.913 59.4987 140.25 59.4987H169.999C172.337 59.4987 174.249 57.5866 174.249 55.2491C174.249 52.9116 172.337 50.9996 169.999 50.9996ZM161.499 67.9997H140.249C137.911 67.9997 135.999 69.9118 135.999 72.2493C135.999 74.5867 137.912 76.4988 140.249 76.4988H161.499C163.836 76.4988 165.748 74.5867 165.748 72.2493C165.75 69.9117 163.836 67.9997 161.499 67.9997ZM178.499 33.9995H140.249C137.912 33.9995 136 35.9116 136 38.249C136 40.5866 137.912 42.4986 140.249 42.4986H178.499C180.836 42.4986 182.748 40.5865 182.748 38.249C182.748 35.9115 180.836 33.9995 178.499 33.9995Z"
                          fill="#128324"
                        />
                      </svg>
                    </div>
                    <p className="text-lg lg:text-xl mb-2 font-semibold ">
                      Thank you for contacting us
                    </p>
                    <p className="text-cstm-grey text-sm lg:text-sm-15 w-11/12 md:w-8/12 lg:w-6/12">
                      We’ll be in touch with you soon via email.
                    </p>
                  </div>
                ) : (
                  <>
                    <form onSubmit={handleSubmit}>
                      <h2 className="text-2xl lg:text-3xl font-semibold text-center mb-6 lg:mb-8">
                        Contact
                      </h2>
                      <div className="flex flex-wrap -mx-3 mb-4">
                        <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                          <label
                            className="block tracking-wide text-xs  text-[#6d6969] mb-2"
                            htmlFor="grid-first-name">
                            First name *
                          </label>
                          <input
                            className="appearance-none bg-[#FDFEFF] block w-full border-[0.309298px] border-[#B3B3B3]  text-[#979797] text-sm rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            id="grid-first-name"
                            type="text"
                            placeholder="John"
                            name="firstname"
                            value={formData.firstname}
                            onChange={handleChange}
                            onFocus={handleFocus}
                          />
                          {errors?.firstname && (
                            <p className="text-red-500 text-sm">
                              {errors?.firstname}
                            </p>
                          )}
                        </div>
                        <div className="w-full md:w-1/2 px-3">
                          <label
                            className="block tracking-wide text-xs  text-[#6d6969] mb-2"
                            htmlFor="grid-last-name">
                            Last name *
                          </label>
                          <input
                            className="appearance-none bg-[#FDFEFF] block w-full border-[0.309298px] border-[#B3B3B3]  text-[#979797] text-sm rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            id="grid-last-name"
                            type="text"
                            placeholder="Doe"
                            name="lastname"
                            value={formData.lastname}
                            onChange={handleChange}
                            onFocus={handleFocus}
                          />
                          {errors?.lastname && (
                            <p className="text-red-500 text-sm">
                              {errors?.lastname}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap -mx-3 mb-4">
                        <div className="w-full px-3">
                          <label
                            className="block tracking-wide text-xs  text-[#6d6969] mb-2"
                            htmlFor="grid-password">
                            E-mail *
                          </label>
                          <input
                            className="appearance-none bg-[#FDFEFF] block w-full border-[0.309298px] border-[#B3B3B3]  text-[#979797] text-sm rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            id="email"
                            type="email"
                            placeholder="johndoe@workmail.com"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onFocus={handleFocus}
                          />
                          {errors?.email && (
                            <p className="text-red-500 text-sm">
                              {errors?.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap -mx-3 mb-4">
                        <div className="w-full px-3">
                          <label
                            className="block tracking-wide text-xs  text-[#6d6969] mb-2"
                            htmlFor="grid-password">
                            Phone *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className="appearance-none bg-[#FDFEFF] block w-full border-[0.309298px] border-[#B3B3B3]  text-[#979797] text-sm rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            placeholder="+234 800 000 0000"
                          />
                          {errors.phone && (
                            <p className="text-sm text-red-500">
                              {errors.phone}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap -mx-3 mb-6">
                        <div className="w-full px-3">
                          <label
                            className="block tracking-wide text-xs  text-[#6d6969] mb-2"
                            htmlFor="grid-password">
                            Message *
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            className="no-resize appearance-none block bg-[#FDFEFF] w-full border-[0.309298px] border-[#B3B3B3]  text-[#979797] text-sm rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 h-48 resize-none"
                            id="message"
                            placeholder="Type Message"></textarea>
                          {errors?.message && (
                            <p className="text-red-500 text-sm">
                              {errors?.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-center">
                        <button
                          className="shadow bg-cstm-green focus:shadow-outline focus:outline-none text-[white]  py-3 px-8 rounded"
                          type="submit"
                          disabled={loading}>
                          {loading ? "Processing..." : "Send Message"}
                        </button>
                      </div>
                    </form>
                    <p className="mt-10 py-2 text-[#6d6969] text-xs text-center flex flex-wrap items-center justify-center">
                      Support & Enquires:{" "}
                      <a
                        href="mailto:hello@reeddi.com"
                        className="text-xs text-cstm-green">
                        &nbsp;hello@reeddi.com&nbsp;
                      </a>{" "}
                      |{" "}
                      <p className="text-xs text-[#6d6969]">
                        &nbsp;(234) 705 044 5599, (234) 915 842 8464
                      </p>
                    </p>
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
